import { IBaseNavRoute, INavRoute } from 'app/components'
import { QuestionsGroupMediator } from 'app/components/questions/mediator'
import { Container } from 'app/containers/Container'
import { QuestionGroups, QuestionGroupsKey } from 'app/containers/PipelineSection/QuestionGroups'
import { IAppState, store } from 'app/redux/App/store'
import { IRetirementPerson, isValidPerson, Profile } from 'app/redux/Profile/model'
import * as utils from 'app/utils'
import { FormParser } from 'app/utils/form/parser'
import { escapeRegexpString } from 'app/utils/regexp';
import { IPipelineConfig, IPipelineInstance } from 'config/interfaces'
import * as _ from 'lodash'
import * as React from 'react'
import { Button } from 'react-bootstrap'
import { Redirect, Route, Switch } from 'react-router'
import { Link } from 'react-router-dom'
import { ShowAlwaysRoutes } from '../RetirementApp/routes'

interface IPipelineSectionNavRoute extends IBaseNavRoute {
  hidePrevious?: boolean
  hideNext?: boolean
  hideNavRoutes?: boolean
}

interface IPipelineSectionProps {
  PipelineInstance: IPipelineInstance
}

interface IPipelineSectionState {
  currentNavIndex: number
  defaultPath: string
  currentNavRoute: IPipelineSectionNavRoute
  NavRoutes: INavRoute[],
  profile: Profile,
}

const debug = utils.makeDebug(false)

class PipelineSection extends Container<IPipelineSectionProps, IPipelineSectionState> {

  /**
   * Convert IPipelineConfig to INavRoute[]
   * Note: Used for left-nav on Dashboard
   */
  static getNavRoutesFor = (config: IPipelineConfig): INavRoute[] => {
    const routes: INavRoute[] = []

    return _.values(config)
      .map((instance) => {
        return {
          single: {
            name: instance.name,
            exact: false,
            path: instance.path,
            icon: instance.icon,
            component: () => (
              <PipelineSection PipelineInstance={instance} />
            )
          }
        }
      })
  }

  state: IPipelineSectionState = {
    currentNavIndex: 0,
    defaultPath: null,
    currentNavRoute: null,
    NavRoutes: [],
    profile: null,
  }

  private unsubscribeFromMediator: () => void
  private unsubscribeFromSaveMediator: () => void
  private member: IRetirementPerson
  private spouse: IRetirementPerson
  private basePath: string

  componentWillMount() {
    const pipelineInstance = this.props.PipelineInstance
    let NavRoutes = this.partialsToNavRoutes(pipelineInstance)
    this.state.defaultPath = NavRoutes[0].single ? NavRoutes[0].single.path : NavRoutes[0].multi.routes[0].path
    this.state.NavRoutes = NavRoutes
    this.basePath = pipelineInstance.path

    //Reroute index route to actual route
    const hash = location.hash.substring(1)
    const escapedPathStr = escapeRegexpString(this.basePath)
    const indexedRouteExp = new RegExp(escapedPathStr + '(\\d+)')
    const matches = indexedRouteExp.exec(hash)

    if (Array.isArray(matches) && matches.length >= 2) {
      const index = Number(matches[1])

      this.goToIndex(index, true)
    }

    //Setup Question Mediator
    this.unsubscribeFromMediator = QuestionsGroupMediator
      .setQuestionValueCallback((questionName) => this.getQuestionValue(questionName))

    this.unsubscribeFromSaveMediator = QuestionsGroupMediator
      .setForceSaveCallback(() => this.saveFormToProfile())

    //Highlight correct submenu item on Location Change
    this.onLocationUpdate((location) => {
      const locationHash = location.hash.substring(1)
      // tslint:disable-next-line:no-debugger
      //debugger

      this.saveFormToProfile()

      this.goToNavRouteWithHashOf(locationHash)
    })

    this.onAppStateUpdate((appState) => {
      const profile = appState.Profile.getNative('info') || null
      if (!profile) { return }
      debug('onAppState update!')
      const spouse = profile.getSpouse()
      const spouseChange = !_.isEqual(this.spouse, spouse)

      this.member = profile.getHeadOfHousehold()
      this.spouse = spouse

      // if there has been a change in the spouse, refresh the routes
      if (spouseChange) {
        debug('refreshing routes because spouse has changed')
        NavRoutes = this.partialsToNavRoutes(pipelineInstance)
      }

      this.setState({ profile, NavRoutes })
    })
  }

  componentWillUnmount() {
    super.componentWillUnmount()

    this.unsubscribeFromMediator()
    this.unsubscribeFromSaveMediator()

    this.Actions.Nav.removeBreadcrumb()
  }

  getQuestionValue(questionName: string) {
    questionName = this.parseQuestionName(questionName)

    const r = _.get(this.state.profile, questionName)
    //debug('getQuestionValue "' + questionName + '" yielded: ' + JSON.stringify(r))
    return r
  }

  parseQuestionName(questionName: string) {
    if (!questionName) { return '' }

    return questionName
      .replace('{member}', this.member.id)
      .replace('{spouse}', this.spouse.id)
  }

  /**
   * Convert IPipelineInstance to INavRoute[]
   * Note: Used for Left Nav
   * @param pipelinInstance
   */
  partialsToNavRoutes(pipelinInstance: IPipelineInstance): INavRoute[] {
    const name2Path = (name: string) => {
      return name.replace(/\W/g, '_').toLowerCase()
    }

    const ggg = store.getState().Profile.getNative('info')
    const activeSections = _.filter(pipelinInstance.sections, (s) => {
      if (!s.needsSpouse) { return true }
      if (!this.spouse) { debug('no profile!'); return true; }
      const r = isValidPerson(this.spouse)
      debug('is spouse valid? ' + r)
      return r
    })
    debug('# sections: ' + activeSections.length);
    const sectionRoutes = activeSections.map((section): INavRoute => {

      if (Array.isArray(section.subsections)) {
        return {
          hidden: section.hidden,
          multi: {
            name: section.name,
            icon: section.icon,
            routes: section.subsections.map((route) => {
              const component = QuestionGroups[route.questionsGroupName]
              const componentInstance = component()
              const name = componentInstance.props.header
              const icon = componentInstance.props.icon
              const subPath = name2Path(section.name) + '/' + name2Path(componentInstance.props.header)
              const path = pipelinInstance.path + subPath
              const hideNext = route.hideNext
              const hidePrevious = route.hidePrevious
              const hideNavRoutes = route.hideNavRoutes
              const nextSectionRoutePath = route.nextSectionRoutePath

              return { hideNext, hidePrevious, hideNavRoutes, name, icon, path, component, nextSectionRoutePath }
            })
          }
        }
      } else if (section.subsection) {
        const route = section.subsection
        const component = QuestionGroups[route.questionsGroupName]
        const componentInstance = component()
        const name = componentInstance.props.header
        const icon = section.icon
        const subPath = name2Path(section.name)
        const path = pipelinInstance.path + subPath
        const hideNext = route.hideNext
        const hidePrevious = route.hidePrevious
        const hideNavRoutes = route.hideNavRoutes
        const nextSectionRoutePath = route.nextSectionRoutePath

        return {
          hidden: section.hidden,
          single: { name, icon, path, component, hideNext, hidePrevious, hideNavRoutes, nextSectionRoutePath }
        }
      }
    })
    return _.concat(sectionRoutes, ShowAlwaysRoutes)
  }

  /**
   * Saved current form on page to Profile
   * @param form
   */
  saveFormToProfile(form?: HTMLFormElement) {
    const { profile } = this.state

    if (!form) {
      form = document.getElementsByTagName('form')[0] as HTMLFormElement
    }

    if (form && profile) {
      FormParser.parseInputsWithPaths(form, (str) => this.parseQuestionName(str), profile)

      this.Actions.Profile.store(profile)
    }
  }

  /**
   * Go to next question
   */
  next(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    this.goToIndex(this.state.currentNavIndex + 1)
  }

  /**
   * Go to previous question
   */
  previous() {
    this.goToIndex(this.state.currentNavIndex - 1)
  }

  /**
   * Go to a question with the given index
   * @param currentNavIndex
   */
  goToIndex(currentNavIndex: number, replace?: boolean) {
    const currentNavRoute = this.getNavRoute(currentNavIndex)

    this.goToNavRoute(currentNavIndex, currentNavRoute, replace)
  }

  /**
   * Go to NavRoute with given route and index
   * @param currentNavIndex
   * @param currentNavRoute
   */
  goToNavRoute(currentNavIndex: number, currentNavRoute: IPipelineSectionNavRoute, replace = false) {
    if (currentNavRoute.hideNavRoutes) {
      this.Actions.Nav.removeBreadcrumb()
      this.Actions.Nav.showDefaultNavRoutes()
    } else {
      this.Actions.Nav.updateBreadcrumb(this.props.PipelineInstance.name, this.props.PipelineInstance.path)
      this.Actions.Nav.updateNavRoutes(this.state.NavRoutes)
    }

    if (replace) {
      this.state.defaultPath = currentNavRoute.path
      this.replace(currentNavRoute.path)

      //kludge to make sure replace is successful
      this.setState({ defaultPath: this.state.defaultPath })
    } else {
      location.hash = '#' + currentNavRoute.path
    }

    this.setState({ currentNavIndex, currentNavRoute })
  }

  /**
   * Go to a question with the given hash
   * @param hash
   */
  goToNavRouteWithHashOf(hash: string) {
    let currentNavIndex = 0

    this.state.NavRoutes.every((route: INavRoute) => {
      if (route.single) {
        if (route.single.path === hash) {
          this.goToNavRoute(currentNavIndex, route.single)

          return false
        }

        currentNavIndex++
        return true
      }

      return route.multi.routes.every((baseRoute) => {
        if (baseRoute.path === hash) {
          this.goToNavRoute(currentNavIndex, baseRoute)

          return false
        }

        currentNavIndex++
        return true
      })
    })
  }

  /**
   * Find NavRoute by index
   * @param findNavIndex
   */
  getNavRoute(findNavIndex: number) {
    let navRoute: IPipelineSectionNavRoute
    let navIndex = 0

    this.state.NavRoutes.every((route: INavRoute) => {
      if (route.single) {
        if (navIndex === findNavIndex) {
          navRoute = route.single

          return false
        }
        navIndex++
        return true
      }

      return route.multi.routes.every((baseRoute) => {
        if (navIndex === findNavIndex) {
          navRoute = baseRoute

          return false
        }

        navIndex++
        return true
      })
    })

    return navRoute
  }

  render() {
    if (!this.state.profile) {
      return null
    }

    const routes = this.state.NavRoutes.map((route) => {
      if (route.single) {
        return (
          <Route key={route.single.path}
            exact={!!route.single.exact}
            path={route.single.path}
            component={route.single.component} />
        )
      } else if (route.multi) {
        return route.multi.routes.map((multiRoute) => (
          <Route key={multiRoute.path}
            exact={!!multiRoute.exact}
            path={multiRoute.path}
            component={multiRoute.component} />
        ))
      }
    })

    return (
      <form onSubmit={(e) => this.next(e)} noValidate className="container-question">
        <section className="material-input-group-lg">
          <Switch>
            {routes}
            <Redirect to={this.state.defaultPath} />
          </Switch>
        </section>

        {this.state.currentNavRoute ?
          <footer>
            {!this.state.currentNavRoute.hidePrevious ?
              <Button type="button" bsStyle="default" bsSize="lg" onClick={() => this.previous()}
                className="animated fadeIn"
                tabIndex={-1}>
                Prev
              </Button>
              : null
            }

            {!this.state.currentNavRoute.hideNext ?
              this.state.currentNavRoute.nextSectionRoutePath ?
                <Link to={this.state.currentNavRoute.nextSectionRoutePath}>
                  <Button type="button" bsStyle="primary" bsSize="lg"
                    className="animated fadeIn">
                    Next Section
                  </Button>
                </Link>
                :
                <Button type="submit" bsStyle="primary" bsSize="lg"
                  className="animated fadeIn">
                  Next
                </Button>
              : null
            }
          </footer>
          : null
        }
      </form>
    )
  }
}

export {
  PipelineSection,
  IPipelineSectionNavRoute
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/index.tsx